import { SpotType } from '@spiaggeit/spit-core'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { CartFooter } from '@/components/CartFooter'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { SelectedElementDrawer } from '@/components/SelectedElementDrawer'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { useFetchPricePreview } from '@/hooks/useFetchPricePreview'
import { useResetInsertInsuranceProducts } from '@/hooks/useResetInsertInsurance'
import { ServiceInfoPurchaseType } from '@/models/service'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice'
import { cartSlice } from '@/store/cartSlice'
import {
  loadMapData,
  loadSectors,
  mapSelectors,
  mapSlice,
  mappedSectorsSelector,
} from '@/store/mapSlice'
import { servicesSlice } from '@/store/servicesSlice'
import { MAP_CELL_SIZE } from '@/utils/constants'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { normalizeReservation } from '@/utils/normalizeReservation'
import { normalizeSpot } from '@/utils/normalizeSpot'

import { SectorCard } from './SectorCard'

export const SectorsRoute = () => {
  const dispatch = useAppDispatch()
  const selectedElement = useAppSelector(mapSelectors.selectedElement)

  const { resetInsertInsuranceProducts } = useResetInsertInsuranceProducts()

  useFetchPricePreview()

  useEffect(() => {
    dispatch(
      loadMapData({
        cellSize: MAP_CELL_SIZE,
        screenHeight: window?.innerHeight ?? 0,
        screenWidth: window?.innerWidth ?? 0,
      })
    )
    dispatch(loadSectors())
    dispatch(
      servicesSlice.actions.load({
        purchaseType: ServiceInfoPurchaseType.WITH_SPOT,
      })
    )

    resetInsertInsuranceProducts()
  }, [])

  useEffect(() => {
    dispatch(cartSlice.actions.setBookingType(BookingAvailability.SPOTS))
  }, [])

  const mappedSectors = useAppSelector(mappedSectorsSelector)
  const isLoading = useAppSelector(mapSelectors.isLoading)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)

  const { t } = useTranslation()

  if (isLoading) {
    return <LoaderTrigger message={t('sectors.loading')} />
  }

  return (
    <>
      <main className="container max-w-5xl grow py-4 lg:py-8">
        <h1 className="mb-1 text-lg font-bold">{t('sectors.title')}</h1>
        <p className="mb-4">{t('sectors.subheading')}</p>

        <div className="mb-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {mappedSectors?.flatMap((sector) => {
            return (
              sector.elementsGroupedByType?.flatMap((group) => {
                const firstAvailableSpot = group.find(
                  (spot) =>
                    !cartReservations.find((reservation) =>
                      getObjectsComparison(
                        normalizeReservation(reservation),
                        normalizeSpot(spot)
                      )
                    )
                )

                const groupSpotsInCart = group.filter((spot) =>
                  cartReservations.find((reservation) =>
                    getObjectsComparison(
                      normalizeReservation(reservation),
                      normalizeSpot(spot)
                    )
                  )
                )

                const onAdd = () => {
                  if (!firstAvailableSpot) return
                  dispatch(
                    mapSlice.actions.selectMapElement(firstAvailableSpot)
                  )
                }

                const onRemove = () => {
                  dispatch(cartSlice.actions.setCartPopoverIsOpen(true))
                }

                return (
                  <SectorCard
                    availableQuantity={group.length - groupSpotsInCart.length}
                    imageUrl={sector.image}
                    key={sector.id + group[0].subType}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    sectorName={sector.name}
                    selectedQuanitity={groupSpotsInCart.length}
                    spotType={group[0].subType as SpotType}
                  />
                )
              }) || []
            )
          })}
        </div>
        {selectedElement && <SelectedElementDrawer />}
      </main>

      <CartFooter />
    </>
  )
}
